import React, { useState, useEffect } from 'react';
import { X, Mail, Calendar, Edit3, Save, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Guided prompts for editing
const LETTER_PROMPTS = [
  "How does it feel to have achieved this?",
  "What are you most proud of?",
  "What does your daily life feel like now?",
  "Who have you become?",
  "What habits or mindset got you here?",
  "What are you grateful for?"
];

const FutureSelfLetter = ({ isOpen, onClose, goal, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [letterBody, setLetterBody] = useState('');
  const [letterDate, setLetterDate] = useState('');

  // Initialize state when goal changes
  useEffect(() => {
    if (goal?.future_self_letter) {
      setLetterBody(goal.future_self_letter.body || '');
      setLetterDate(goal.future_self_letter.date || '');
    } else {
      setLetterBody('');
      setLetterDate('');
    }
    setIsEditing(false);
  }, [goal]);

  const hasLetter = goal?.future_self_letter?.body;

  const handleSave = async () => {
    if (!letterBody.trim()) {
      toast.error('Please write your letter');
      return;
    }
    if (!letterDate) {
      toast.error('Please add a date to your letter');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/goals/${goal.goal_id}/future-letter`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          body: letterBody.trim(),
          date: letterDate
        }),
      });

      if (response.ok) {
        const updatedGoal = await response.json();
        toast.success('Letter saved');
        setIsEditing(false);
        if (onUpdate) {
          onUpdate(updatedGoal);
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save letter');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save letter');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      style={{
        paddingTop: 'max(1rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isEditing) {
          onClose();
        }
      }}
    >
      <div 
        className="relative w-full max-w-lg max-h-[90dvh] overflow-y-auto bg-[#1C1C1E] rounded-[28px] border border-white/10 shadow-2xl"
        data-testid="future-letter-modal"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#1C1C1E] px-6 pt-6 pb-4 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C6A75E]/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#C6A75E]" />
              </div>
              <div>
                <h2 className="font-serif text-xl text-[#DCD3CA]">Letter From Your Future Self</h2>
                <p className="text-[#7A7670] text-xs font-sans">{goal?.title}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 text-[#B5B1AB]"
              data-testid="close-letter-btn"
              aria-label="Close letter"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!hasLetter && !isEditing ? (
            // Empty State
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-[#C6A75E]/10 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-[#C6A75E]/50" />
              </div>
              <p className="text-[#B5B1AB] font-sans mb-6">
                You haven't written your future self letter yet.
              </p>
              <Button
                onClick={() => setIsEditing(true)}
                className="bg-[#C6A75E] hover:bg-[#C6A75E]/90 text-[#1C1C1E] font-sans font-medium rounded-2xl px-6 py-3"
                data-testid="write-letter-btn"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Write Your Letter
              </Button>
            </div>
          ) : isEditing ? (
            // Edit Mode
            <div className="space-y-6">
              {/* Prompts */}
              <div className="bg-[#252528]/50 border border-white/5 rounded-2xl p-4">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-3 font-sans">Consider these as you write:</p>
                <div className="grid grid-cols-1 gap-2">
                  {LETTER_PROMPTS.map((prompt) => (
                    <div key={prompt} className="flex items-start gap-2 text-[#B5B1AB] text-sm font-sans">
                      <span className="text-[#8BAA9E] mt-0.5">•</span>
                      <span>{prompt}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Letter Date */}
              <div>
                <label className="text-[#B5B1AB] text-sm font-sans mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date of Your Letter (from the future)
                </label>
                <Input
                  type="date"
                  value={letterDate}
                  onChange={(e) => setLetterDate(e.target.value)}
                  className="w-full bg-[#252528] border-white/10 rounded-2xl py-4 px-5 text-[#DCD3CA] focus:border-[#C6A75E]"
                  data-testid="edit-letter-date"
                />
              </div>

              {/* Letter Body */}
              <div>
                <label className="text-[#B5B1AB] text-sm font-sans mb-2 block">Your Letter</label>
                <Textarea
                  placeholder="Dear present me,

I'm writing to you from the future..."
                  value={letterBody}
                  onChange={(e) => setLetterBody(e.target.value)}
                  className="w-full bg-[#252528] border-white/10 rounded-2xl py-5 px-5 text-[#DCD3CA] placeholder-[#7A7670] focus:border-[#C6A75E] min-h-[250px] resize-none font-sans leading-relaxed"
                  data-testid="edit-letter-body"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setLetterBody(goal?.future_self_letter?.body || '');
                    setLetterDate(goal?.future_self_letter?.date || '');
                    setIsEditing(false);
                  }}
                  variant="outline"
                  className="flex-1 py-4 rounded-2xl border-white/10 text-[#B5B1AB] hover:bg-white/5"
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || !letterBody.trim() || !letterDate}
                  className="flex-1 py-4 rounded-2xl bg-[#C6A75E] hover:bg-[#C6A75E]/90 text-[#1C1C1E] font-sans font-medium"
                  data-testid="save-letter-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Letter
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            // View Mode
            <div className="space-y-6">
              {/* Letter Date */}
              <div className="flex items-center gap-2 text-[#C6A75E]">
                <Calendar className="w-4 h-4" />
                <span className="font-sans text-sm">{formatDate(goal?.future_self_letter?.date)}</span>
              </div>

              {/* Letter Content */}
              <div className="bg-[#252528]/30 border border-white/5 rounded-2xl p-6">
                <p className="text-[#DCD3CA] font-sans leading-relaxed whitespace-pre-wrap">
                  {goal?.future_self_letter?.body}
                </p>
              </div>

              {/* Edit Button */}
              <Button
                onClick={() => setIsEditing(true)}
                variant="outline"
                className="w-full py-4 rounded-2xl border-[#C6A75E]/30 text-[#C6A75E] hover:bg-[#C6A75E]/10"
                data-testid="edit-letter-btn"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit Letter
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FutureSelfLetter;
